import { useEffect, useState } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';

import { auth } from '@/lib/firebase';

type FirebaseAuthState = {
  currentUser: User | null;
  /** True once Firebase Auth has resolved its async session restore at least once. */
  ready: boolean;
};

/**
 * `auth.currentUser` reads `null` for a moment after every page load/reload - Firebase Auth
 * restores its session from local persistence asynchronously, and reading the property directly
 * at render time (the previous pattern across the chat feature) races that restore. Any write
 * built from `Number(auth.currentUser?.uid)` during that window serializes to `NaN`, which
 * Firestore accepts and later renders as `null` on the wire, breaking that conversation for good.
 *
 * This hook exposes the same value reactively via `onAuthStateChanged`, plus `ready` so callers
 * can gate actions until the restore has actually completed - re-rendering alone does not.
 */
export function useFirebaseAuthUser(): FirebaseAuthState {
  const [state, setState] = useState<FirebaseAuthState>({
    currentUser: auth.currentUser,
    ready: auth.currentUser !== null,
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setState({ currentUser: user, ready: true });
    });
    return unsubscribe;
  }, []);

  return state;
}

export default useFirebaseAuthUser;
